import { type ErrorInfo, type ReactNode, useCallback } from 'react';

import { sentryCaptureException } from '@/utils/sentry';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';

import { AppErrorFallback } from './app-error-fallback';

interface AppErrorBoundaryProps {
  children: ReactNode;
  /**
   * Custom fallback to render when an error is caught.
   * Defaults to `AppErrorFallback` which shows an inline error message
   * with a retry button and optional error details toggle.
   */
  fallback?: ReactNode;
  /**
   * Render function for interactive error fallbacks. Receives the error
   * and a resetErrorBoundary callback. When provided, takes precedence
   * over `fallback`.
   */
  fallbackRender?: (props: FallbackProps) => ReactNode;
  /**
   * Additional error handler called alongside the default Sentry report.
   * Useful for tool-specific logging or recovery logic.
   */
  onError?: (error: Error, info: ErrorInfo) => void;
  /**
   * When any value in this array changes, the error boundary resets
   * and attempts to re-render its children. Useful for resetting
   * on route changes (e.g. pass `[location.pathname]`).
   */
  resetKeys?: unknown[];
}

function handleError(error: Error, info: ErrorInfo) {
  sentryCaptureException(error, {
    extra: { componentStack: info.componentStack },
  });
}

/**
 * A generic error boundary that reports errors to Sentry and renders
 * a sensible fallback UI. Drop this around any feature or section to
 * prevent it from crashing the entire page.
 *
 * @example
 * // With default fallback (inline error message + retry + error details)
 * <AppErrorBoundary>
 *   <SomeFeature />
 * </AppErrorBoundary>
 *
 * @example
 * // With custom fallback
 * <AppErrorBoundary fallback={<Text>Something went wrong</Text>}>
 *   <SomeFeature />
 * </AppErrorBoundary>
 *
 * @example
 * // Silent fallback (swallow errors, still reports to Sentry)
 * <AppErrorBoundary fallback={null}>
 *   <ExperimentalFeature />
 * </AppErrorBoundary>
 *
 * @example
 * // Reset on route change
 * const location = useLocation();
 * <AppErrorBoundary resetKeys={[location.pathname]}>
 *   <Outlet />
 * </AppErrorBoundary>
 */
export const AppErrorBoundary = ({ children, fallback, fallbackRender, onError, resetKeys }: AppErrorBoundaryProps) => {
  const combinedOnError = useCallback(
    (error: Error, info: ErrorInfo) => {
      handleError(error, info);
      onError?.(error, info);
    },
    [onError],
  );

  if (fallbackRender != null) {
    return (
      <ErrorBoundary fallbackRender={fallbackRender} onError={combinedOnError} resetKeys={resetKeys}>
        {children}
      </ErrorBoundary>
    );
  }

  if (fallback !== undefined) {
    return (
      <ErrorBoundary fallback={fallback ?? <></>} onError={combinedOnError} resetKeys={resetKeys}>
        {children}
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary FallbackComponent={AppErrorFallback} onError={combinedOnError} resetKeys={resetKeys}>
      {children}
    </ErrorBoundary>
  );
};
